import React, { useCallback, useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { adminApi } from "@/lib/api";
import { LinesTable } from "./LinesTable";
import { Pill, Stat, aud, num } from "./ui";

export function CategoryDrawer({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => adminApi.get(`/admin/wi/categories/${id}`).then(({ data }) => setData(data)).catch(() => setData(null)), [id]);
  useEffect(() => { load(); }, [load]);
  const c = data?.category;
  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="bg-[#0A0A0A] border-neutral-800 text-neutral-200 w-full sm:max-w-3xl overflow-y-auto" data-testid="wi-category-drawer">
        {!c ? <div className="h-40 animate-pulse" /> : (
          <>
            <SheetHeader>
              <SheetTitle className="font-heading text-white text-left">{c.name}</SheetTitle>
              <SheetDescription className="text-left text-neutral-500">{c.system} · {num(c.n_lines)} repair lines · {c.spec_variants} spec variants</SheetDescription>
            </SheetHeader>
            <div className="flex gap-2 mt-3">{c.reliable ? <Pill tone="green">Reliable benchmark</Pill> : <Pill tone="amber">Insufficient comparables — not eligible for auto-approval</Pill>}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <Stat label="Average (ex GST)" value={aud(c.mean, 2)} sub={`${num(c.n_comparable)} comparable`} tone="gold" />
              <Stat label="Median" value={aud(c.median, 2)} />
              <Stat label="Range" value={c.min !== null ? `${aud(c.min)}–${aud(c.max)}` : "—"} />
              <Stat label="±Tolerance band" value={c.tolerance_low ? `${aud(c.tolerance_low)}–${aud(c.tolerance_high)}` : "—"} tone="green" />
              <Stat label="Avg labour $" value={aud(c.labour_cost_avg, 2)} />
              <Stat label="Avg parts $" value={aud(c.parts_cost_avg, 2)} />
              <Stat label="Avg labour hrs" value={c.labour_hours_avg ?? "—"} />
              <Stat label="Avg labour rate" value={aud(c.labour_rate_avg, 2)} />
            </div>
            {c.trend?.length > 1 && (
              <div className="h-40 mt-5" data-testid="wi-category-trend">
                <div className="text-[0.62rem] uppercase tracking-[0.16em] font-bold text-neutral-500 mb-1">Average cost by half-year</div>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={c.trend}><XAxis dataKey="period" stroke="#525252" fontSize={11} /><YAxis stroke="#525252" fontSize={11} width={48} />
                    <Tooltip contentStyle={{ background: "#0F0F0F", border: "1px solid #262626", fontSize: 12 }} formatter={(v) => aud(v, 2)} />
                    <Bar dataKey="mean" fill="#d9b45b" radius={[4, 4, 0, 0]} /></BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="mt-6"><LinesTable lines={data.lines} onSaved={() => { load(); onChanged?.(); }} /></div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
